/**
 * Warehouse domain service.
 *
 * Wraps the trusted pick/pack/ship RPCs. Pick tasks, packing verification and
 * shipment status transitions are the transactional boundary of the warehouse;
 * the UI never manipulates these tables directly.
 */

import { callRpc } from "@/lib/api/rpc";

export function createPickTask(soId: string): Promise<string> {
  return callRpc<string>("create_pick_task", { p_so_id: soId });
}

export function createShipmentHeader(fulfilmentId: string): Promise<string> {
  return callRpc<string>("create_shipment_header", { p_fulfilment_id: fulfilmentId });
}

export function startPickTask(pickTaskId: string): Promise<void> {
  return callRpc("start_pick_task", { p_pick_task_id: pickTaskId });
}

export function recordPickItem(
  pickItemId: string,
  qty: number,
  isIncrement: boolean,
): Promise<void> {
  return callRpc("record_pick_item", {
    p_pick_item_id: pickItemId,
    p_qty: qty,
    p_is_increment: isIncrement,
  });
}

export function completePickTask(pickTaskId: string): Promise<void> {
  return callRpc("complete_pick_task", { p_pick_task_id: pickTaskId });
}

export function reassignPickTask(pickTaskId: string): Promise<void> {
  return callRpc("reassign_pick_task", { p_pick_task_id: pickTaskId });
}

export function cancelPickTask(pickTaskId: string): Promise<void> {
  return callRpc("cancel_pick_task", { p_pick_task_id: pickTaskId });
}

export interface PackVerificationResult {
  ok?: boolean;
  error?: string | null;
  message?: string | null;
  remaining?: number;
  required?: number;
  already_verified?: number;
}

export function recordPackVerification(
  orgId: string,
  fulfilmentId: string,
  variantId: string,
  qtyIncrement: number,
): Promise<PackVerificationResult> {
  return callRpc<PackVerificationResult>("record_pack_verification", {
    p_org_id: orgId,
    p_fulfilment_id: fulfilmentId,
    p_variant_id: variantId,
    p_qty_increment: qtyIncrement,
  });
}

export function updateShipmentStatus(
  shipmentId: string,
  status: string,
  eventDescription?: string | null,
): Promise<void> {
  return callRpc("update_shipment_status", {
    p_shipment_id: shipmentId,
    p_status: status,
    p_event_description: eventDescription ?? null,
  });
}

export interface RecordedShipmentTracking {
  ok: true;
  tracking_number: string;
  provider_sync_status: string;
  shipping_provider?: string;
  carrier_code?: string;
}

export async function setShipmentTracking(
  shipmentId: string,
  trackingNumber: string,
  carrierCode?: string,
  carrierLabel?: string | null,
): Promise<RecordedShipmentTracking> {
  const value = await callRpc<unknown>("set_shipment_tracking", {
    p_shipment_id: shipmentId,
    p_tracking_number: trackingNumber,
    p_carrier_code: carrierCode ?? "auspost",
    p_carrier_label: carrierLabel ?? null,
  });
  const result = value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
  if (result.ok === false && typeof result.error === "string") throw new Error(result.error);
  if (result.ok !== true || typeof result.tracking_number !== "string" || result.tracking_number !== trackingNumber.trim()
    || typeof result.provider_sync_status !== "string"
    || !["local_recorded", "pending", "confirmed", "error", "remote_review"].includes(result.provider_sync_status)) {
    throw new Error("Could not confirm that tracking was saved. Refresh the shipment before retrying.");
  }
  return {
    ok: true,
    tracking_number: result.tracking_number,
    provider_sync_status: result.provider_sync_status,
    shipping_provider: typeof result.shipping_provider === "string" ? result.shipping_provider : undefined,
    carrier_code: typeof result.carrier_code === "string" ? result.carrier_code : undefined,
  };
}
